import { createReducer, on } from '@ngrx/store';
import { createDefaultLoadable, onLoadableLoad, onLoadableSuccess, onLoadableError } from '../../common/models/loadable';
import { AGGREGATE_PAGE_SIZE } from '../../common/constants/filter';
import { AGGREGATE_PRESETS, buildDrilldownStepFromRow } from '../models/aggregate.models';
import { AggregateActions } from './aggregate.actions';
import { AggregateState, initialAggregateState } from './aggregate.state';

export type { AggregateState };
export { initialAggregateState };

export const aggregateReducer = createReducer(
  initialAggregateState,

  on(AggregateActions.openAggregate, (state) => ({
    ...state,
    ...createDefaultLoadable(),
    active: true,
    hasPendingChanges: false,
    metricsHavingModified: false,
    groupBy: [{ key: 'site_id' }],
    committedGroupBy: [{ key: 'site_id' }],
    aggregates: AGGREGATE_PRESETS.map(p => ({ ...p })),
    having: [],
    ordering: [{ key: 'alert_count', direction: 'DESC' as const }],
    pagination: { per_page: AGGREGATE_PAGE_SIZE, page_num: 1 },
    drilldownPath: [],
    selectedGroups: [],
    results: [],
    totalGroups: 0,
    totalPages: 0,
  })),

  on(AggregateActions.closeAggregate, () => ({ ...initialAggregateState })),

  on(AggregateActions.setGroupBy, (state, { groupBy }) => ({
    ...state,
    hasPendingChanges: true,
    groupBy,
    drilldownPath: [],
  })),

  on(AggregateActions.setAggregates, (state, { aggregates }) => ({
    ...state,
    hasPendingChanges: true,
    aggregates,
  })),

  on(AggregateActions.setHaving, (state, { having }) => ({
    ...state,
    hasPendingChanges: true,
    having,
  })),

  on(AggregateActions.setOrdering, (state, { ordering }) => ({
    ...state,
    hasPendingChanges: true,
    ordering,
  })),

  on(AggregateActions.setPagination, (state, { pagination }) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    pagination,
  })),

  on(AggregateActions.toggleGroupSelection, (state, { group }) => {
    const exists = state.selectedGroups.some(
      (g) => g.key === group.key && g.value === group.value,
    );
    return {
      ...state,
      selectedGroups: exists
        ? state.selectedGroups.filter((g) => !(g.key === group.key && g.value === group.value))
        : [...state.selectedGroups, group],
    };
  }),

  on(AggregateActions.selectAllGroups, (state) => {
    const spec = state.committedGroupBy[state.drilldownPath.length];
    if (!spec) {
      return state;
    }
    return {
      ...state,
      selectedGroups: state.results.map((row) => buildDrilldownStepFromRow(row, spec)),
    };
  }),

  on(AggregateActions.clearGroupSelection, (state) => ({
    ...state,
    selectedGroups: [],
  })),

  on(AggregateActions.pushDrilldown, (state, { step }) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    drilldownPath: [...state.drilldownPath, step],
    pagination: { ...state.pagination, page_num: 1 },
  })),

  on(AggregateActions.popDrilldown, (state) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    drilldownPath: state.drilldownPath.slice(0, -1),
    pagination: { ...state.pagination, page_num: 1 },
  })),

  on(AggregateActions.clearDrilldown, (state) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    drilldownPath: [],
    pagination: { ...state.pagination, page_num: 1 },
  })),

  on(AggregateActions.truncateDrilldown, (state, { depth }) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    drilldownPath: state.drilldownPath.slice(0, depth + 1),
    pagination: { ...state.pagination, page_num: 1 },
  })),

  on(AggregateActions.runAggregate, (state) => ({
    ...onLoadableLoad(state),
    isAppending: false,
    ...(state.hasPendingChanges ? { drilldownPath: [], pagination: { ...state.pagination, page_num: 1 } } : {}),
  })),

  on(AggregateActions.loadNextPage, (state) => ({
    // Keep success:true so the grid stays visible while appending; onLoadableLoad would reset it
    ...onLoadableLoad(state),
    success: true,
    isAppending: true,
    pagination: { ...state.pagination, page_num: state.pagination.page_num + 1 },
  })),

  on(AggregateActions.runAggregateSuccess, (state, { response }) => ({
    ...onLoadableSuccess(state),
    hasPendingChanges: false,
    committedGroupBy: state.groupBy,
    isAppending: false,
    // A non-append load replaces the visible groups, so any prior group selection no longer applies.
    selectedGroups: state.isAppending ? state.selectedGroups : [],
    results: state.isAppending ? [...state.results, ...response.data] : response.data,
    totalGroups: response.pagination.total_groups,
    totalPages: response.pagination.total_pages,
  })),

  on(AggregateActions.runAggregateFailure, (state, { error }) => ({
    ...onLoadableError(state, error),
    isAppending: false,
  })),

  on(AggregateActions.leafAlertsLoaded, (state) => ({
    ...onLoadableSuccess(state),
  })),

  on(AggregateActions.markMetricsHavingModified, (state) => ({
    ...state,
    metricsHavingModified: true,
  })),
);
